import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/components";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/context/i18n";
import { queryClient } from "@/lib/api";

import { settingsApi } from "./settings.api";
import { ISettingsHooks } from "./settings.hooks.interface";
import { passwordSchema, profileSchema } from "./settings.schema";
import {
  IUserProfileResponse,
  TPasswordRequest,
  TUpdateProfileRequest,
} from "./settings.types";

export const useSettings = (): ISettingsHooks => {
  const { toast } = useToast();
  const { auth } = useAuth();
  const { t } = useI18n();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const qrCodeRef = React.useRef<HTMLDivElement>(null);

  const [croppedImage, setCroppedImage] = React.useState<File | undefined>(
    undefined
  );
  const [isQrCodeVisible, setIsQrCodeVisible] = React.useState<boolean>(false);

  const mfaMethodOptions = React.useMemo(
    () => [
      { value: "EMAIL", label: "E-mail" },
      { value: "APP", label: t("settings.profile.mfaMethodApp") },
    ],
    [t]
  );

  const profileForm = useForm<TUpdateProfileRequest>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: auth.name || "",
      mfaEnabled: 0,
      mfaMethod: undefined,
      mfaKey: undefined,
      picturePath: undefined,
      file: undefined,
      personType: "COMPANY",
      document: "",
      whatsapp: "",
    },
  });

  const file = profileForm.watch("file");
  const mfaEnabled = profileForm.watch("mfaEnabled");
  const mfaKey = profileForm.watch("mfaKey");
  const personType = profileForm.watch("personType");

  const personTypeOptions = React.useMemo(
    () => [
      { value: "COMPANY", label: t("settings.profile.personTypeCompany") },
      { value: "PERSON", label: t("settings.profile.personTypePerson") },
    ],
    [t]
  );

  const passwordForm = useForm<TPasswordRequest>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { data: user, error: userError } = useQuery<IUserProfileResponse>({
    queryKey: ["user", auth.userId],
    queryFn: () => settingsApi.getUserProfile(auth.userId),
    enabled: !!auth.userId,
  });

  const shouldRenderPasswordForm = React.useMemo(() => {
    if (!user) return false;
    return user.provider === "api";
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    profileForm.reset({
      name: user.name,
      mfaEnabled: user.mfaEnabled,
      mfaMethod: user.mfaMethod,
      picturePath: user.userPicture,
      file: undefined,
      mfaKey: user.mfaKey,
      personType: user.personType,
      document: user.document || "",
      whatsapp: user.whatsapp || "",
    });
  }, [user, profileForm]);

  React.useEffect(() => {
    if (userError) {
      toast({
        title: t("generic.error"),
        description: userError.message,
        variant: "destructive",
      });
    }
  }, [userError, toast, t]);

  React.useEffect(() => {
    const subscription = profileForm.watch((value, { name }) => {
      if (name === "mfaMethod" && value.mfaMethod === "APP") {
        setIsQrCodeVisible(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [profileForm]);

  const onCloseQrCode = React.useCallback(() => {
    setIsQrCodeVisible((prev) => !prev);
  }, []);

  const onCropComplete = React.useCallback((croppedImage: File) => {
    setCroppedImage(croppedImage);
  }, []);

  const updateProfile = useMutation({
    mutationFn: (data: TUpdateProfileRequest) =>
      settingsApi.updateProfile(auth.userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", auth.userId] });
      toast({
        title: t("generic.success"),
        description: t("settings.profileUpdated"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmitProfile = React.useCallback(
    async (data: TUpdateProfileRequest) => {
      await updateProfile.mutateAsync({
        ...data,
        file: croppedImage ? croppedImage : data.file,
      });
    },
    [updateProfile, croppedImage]
  );

  const updatePassword = useMutation({
    mutationFn: (data: TPasswordRequest) =>
      settingsApi.changePassword(auth.userId, data),
    onSuccess: () => {
      toast({
        title: t("generic.success"),
        description: t("settings.security.passwordUpdated"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmitPassword = React.useCallback(
    async (data: TPasswordRequest) => {
      updatePassword.mutate(data);
    },
    [updatePassword]
  );

  return {
    shouldRenderPasswordForm,
    profileForm,
    passwordForm,
    mfaMethodOptions,
    file,
    fileInputRef,
    onCropComplete,
    onSubmitProfile,
    mfaEnabled,
    mfaKey,
    onSubmitPassword,
    qrCodeRef,
    isQrCodeVisible,
    onCloseQrCode,
    personTypeOptions,
    personType,
  };
};
